import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

const TYPES = [
  // "Package pricelist" rather than "package": the value `package` means a
  // pricelist holding SEVERAL packages, which is what a customer chooses between.
  ['package', 'Package pricelist', 'Several packages a customer picks from — the usual choice.'],
  ['print', 'Single print', 'One print, sold on its own.'],
  ['digital', 'Digital download', 'Digital images rather than prints.'],
  ['addon', 'Add-on', 'Attaches to something already in the cart (e.g. retouching).'],
  ['printess', 'Personalised item', 'Designed by the customer in the Printess editor.'],
];

// The server's product_params reads a whole shop_product hash, so every save
// sends the current values rather than one changed key.
// Each control sends ONLY its own field. This step used to resend every field on
// every keystroke, spread from the `product` prop, to stop the others being blanked.
// They were never at risk -- apply_basics uses assign_attributes, which writes only
// the keys present -- and resending was actively harmful: each payload carried the
// other fields' STALE values, so two edits inside the debounce window overwrote each
// other. Pinned by "leaves the fields a partial save does not mention alone" in
// spec/requests/admin/shop/products_wizard_spec.rb.

// Step 1. Everything the customer reads, plus the two facts the rest of the setup
// depends on: what kind of thing this is, and which season it sells for.
const Basics = ({
  product, choices, commit, autosave, setOverrides,
}) => (
  <Stack spacing={2}>
    <Box>
      <Typography variant="subtitle2" gutterBottom>What kind of product is this?</Typography>
      <Stack spacing={0.5}>
        {TYPES.map(([value, title, blurb]) => {
          const on = product.product_type === value;
          return (
            <Paper
              key={value}
              sx={{
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                borderColor: on ? 'primary.main' : 'divider',
                bgcolor: on ? 'action.selected' : 'background.paper',
              }}
              onClick={() => commit({ shop_product: { product_type: value } })}
            >
              <FormControlLabel
                control={<Radio checked={on} onChange={() => {}} />}
                sx={{ m: 0, alignItems: 'flex-start' }}
                label={(
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{title}</Typography>
                    <Typography variant="caption" color="text.secondary">{blurb}</Typography>
                  </Box>
                )}
              />
            </Paper>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Changing this can add or remove later steps.
      </Typography>
    </Box>

    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
      <TextField
        label="Name customers see"
        placeholder="e.g. Ultimate Collection"
        defaultValue={product.name || ''}
        sx={{ flex: '1 1 260px' }}
        onChange={(e) => {
          setOverrides((o) => ({ ...o, name: e.target.value }));
          autosave({ shop_product: { name: e.target.value } });
        }}
      />
      {/* Autocomplete, not a select: there are 45 seasons and the list opened
          taller than the screen. MUI's own, rather than the react-select used on
          the create page, so the island keeps to one component library -- both read
          the SAME ranked and grouped data from Shop::SeasonChoices.

          Free text is off (no `freeSolo`) on purpose: a season is matched against
          student_images.folder exactly, so a typo makes a product no student can
          ever see and nothing surfaces it. */}
      <Autocomplete
        options={choices.seasons}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label || option.value || ''}
        isOptionEqualToValue={(option, chosen) => option.value === chosen.value}
        value={choices.seasons.find((s) => s.value === product.season) || null}
        sx={{ flex: '1 1 260px' }}
        onChange={(_event, chosen) => commit({
          shop_product: { season: chosen ? chosen.value : '' },
        })}
        renderInput={(params) => (
          <TextField
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...params}
            label="Season (graduation year)"
            helperText="Named by graduation year: autumn 2025 photos are the 2026 season."
          />
        )}
      />
    </Stack>

    {/* No Printess design is asked for here. It is per ITEM, on "What the customer
        gets" -- a personalised product is a set, and the mug and the magnet are
        different designs. A product-level default existed briefly and only invited
        the question at the wrong level (#5447). */}

    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
      <TextField
        label="Short line under the name"
        defaultValue={product.tagline || ''}
        sx={{ flex: '1 1 260px' }}
        onChange={(e) => autosave({ shop_product: { tagline: e.target.value } })}
      />
      <TextField
        label="Longer description"
        multiline
        rows={2}
        defaultValue={product.description || ''}
        sx={{ flex: '1 1 260px' }}
        onChange={(e) => autosave({ shop_product: { description: e.target.value } })}
      />
    </Stack>
  </Stack>
);

Basics.propTypes = stepPropTypes;
Basics.defaultProps = stepDefaultProps;

export default Basics;
